import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Registration } from '../../types/registration';
import './PaymentSuccess.css';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const registrationId = searchParams.get('registrationId');
  const sessionId = searchParams.get('session_id');

  const [registration, setRegistration] = useState<Registration | null>(null);
  const [allRegistrations, setAllRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentPayment, setRecentPayment] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!registrationId) {
        setError('Registration ID is required');
        setLoading(false);
        return;
      }

      try {
        // Fetch the current registration
        const registrationDoc = await getDoc(doc(db, 'Registrations', registrationId));

        if (!registrationDoc.exists()) {
          setError('Registration not found');
          setLoading(false);
          return;
        }

        const regData = {
          id: registrationDoc.id,
          ...registrationDoc.data(),
        } as Registration;

        setRegistration(regData);

        // Find recent payment (CreditCard payment, most recent)
        const paymentHistory = regData.paymentHistory || [];
        const creditCardPayments = paymentHistory
          .filter((p) => p.method === 'CreditCard')
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        if (creditCardPayments.length > 0) {
          setRecentPayment(creditCardPayments[0]);
        }

        // Fetch all registrations for this camper (by email)
        if (regData.email) {
          const allRegsQuery = query(
            collection(db, 'Registrations'),
            where('email', '==', regData.email),
            orderBy('submittedAt', 'desc')
          );
          const allRegsSnapshot = await getDocs(allRegsQuery);
          const allRegs = allRegsSnapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() } as Registration)
          );
          setAllRegistrations(allRegs);
        } else {
          setAllRegistrations([regData]);
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load payment information');
        setLoading(false);
      }
    };

    fetchData();
  }, [registrationId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (dateValue: any) => {
    let parsedDate: Date;

    if (dateValue && typeof dateValue.toDate === 'function') {
      parsedDate = dateValue.toDate();
    } else if (dateValue && typeof dateValue.seconds === 'number') {
      parsedDate = new Date(dateValue.seconds * 1000);
    } else {
      parsedDate = new Date(dateValue);
    }

    if (Number.isNaN(parsedDate.getTime())) {
      return 'Invalid Date';
    }

    return parsedDate.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateTotalFees = (reg: Registration): number => {
    const fin = reg.financial as any;
    if (!fin) return 0;

    const accommodationFee = Number(fin.accommodationFees?.accommodationFee) || 0;
    const accommodationOverride = fin.overrides?.accommodation?.enabled
      ? (Number(fin.overrides.accommodation.amount) || 0) : 0;

    let registrationBase = 0;
    for (const cat of ['adults', 'adultStudents', 'youthChildren']) {
      for (const camper of (fin.registrationFees?.[cat] || [])) {
        registrationBase += Number(camper.fee) || 0;
      }
    }
    const registrationOverride = fin.overrides?.registration?.enabled
      ? (Number(fin.overrides.registration.amount) || 0) : 0;

    const meals = fin.otherFees?.meals;
    const mealBase =
      ((meals?.fullCampPassesAdult?.quantity || 0) * (meals?.fullCampPassesAdult?.unitPrice || 0)) +
      ((meals?.fullCampPassesChild?.quantity || 0) * (meals?.fullCampPassesChild?.unitPrice || 0)) +
      ((meals?.dailyPassesAdult?.quantity || 0) * (meals?.dailyPassesAdult?.unitPrice || 0)) +
      ((meals?.dailyPassesChild?.quantity || 0) * (meals?.dailyPassesChild?.unitPrice || 0));
    const mealOverride = fin.overrides?.mealFees?.enabled
      ? (Number(fin.overrides.mealFees.amount) || 0) : 0;

    const fridgeRental = Number(fin.otherFees?.fridge?.cost) || 0;
    const donation = Number(fin.otherFees?.donations?.amount) || 0;
    const customFee = Number(fin.otherFees?.customFee?.amount) || 0;
    const otherFeesArray = (fin.otherFees?.otherFees || []).reduce(
      (sum: number, f: any) => sum + (Number(f.amount) || 0), 0
    );
    const otherOverride = fin.overrides?.otherFees?.enabled
      ? (Number(fin.overrides.otherFees.amount) || 0) : 0;

    return accommodationFee + accommodationOverride +
      registrationBase + registrationOverride +
      mealBase + mealOverride +
      fridgeRental + donation + customFee + otherFeesArray + otherOverride;
  };

  const getAllPayments = (): any[] => {
    const allPayments: any[] = [];
    allRegistrations.forEach((reg) => {
      const payments = reg.paymentHistory || [];
      payments.forEach((payment) => {
        allPayments.push({
          ...payment,
          registrationId: reg.id,
          registrationYear: reg.year || 'Unknown',
        });
      });
    });
    return allPayments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  if (loading) {
    return (
      <div className="payment-success-page">
        <div className="payment-success-container">
          <div className="loading">Loading payment information...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-page">
        <div className="payment-success-container">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const allPayments = getAllPayments();
  const currentRegPayments = registration?.paymentHistory || [];
  const totalPaid = currentRegPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const balance = registration
    ? Math.max(0, calculateTotalFees(registration) - totalPaid)
    : 0;

  return (
    <div className="payment-success-page">
      <div className="payment-success-container">
        <div className="success-header">
          <h1>✓ Payment Successful</h1>
          {recentPayment && (
            <div className="success-message">
              <p>
                <strong>SUCCESS:</strong> {formatCurrency(recentPayment.amount)} has been paid on
                your account.
              </p>
              {recentPayment.convenienceFee && (
                <p>
                  <small>
                    Convenience Fee: {formatCurrency(recentPayment.convenienceFee)}
                  </small>
                </p>
              )}
              {recentPayment.stripePaymentId && (
                <p>
                  <small>Transaction ID: {recentPayment.stripePaymentId}</small>
                </p>
              )}
            </div>
          )}
        </div>

        {registration && (
          <div className="account-summary">
            <h2>Account Summary</h2>
            <div className="summary-row">
              <span>Total Paid:</span>
              <span>{formatCurrency(totalPaid)}</span>
            </div>
            <div className="summary-row">
              <span>Remaining Balance:</span>
              <span>{formatCurrency(balance)}</span>
            </div>
          </div>
        )}

        <div className="payment-history">
          <h2>Payment History</h2>
          {allPayments.length === 0 ? (
            <p>No payment history found.</p>
          ) : (
            <div className="payments-list">
              {allPayments.map((payment, index) => {
                const isRecent = recentPayment && payment.id === recentPayment.id;
                return (
                  <div
                    key={`${payment.registrationId}-${payment.id}`}
                    className={`payment-item ${isRecent ? 'highlighted' : ''}`}
                  >
                    <div className="payment-header">
                      <div className="payment-method">{payment.method}</div>
                      <div className="payment-amount">{formatCurrency(payment.amount)}</div>
                    </div>
                    <div className="payment-details">
                      <div className="payment-date">{formatDate(payment.date)}</div>
                      {payment.convenienceFee && (
                        <div className="payment-fee">
                          Convenience Fee: {formatCurrency(payment.convenienceFee)}
                        </div>
                      )}
                      {payment.referenceNote && (
                        <div className="payment-note">{payment.referenceNote}</div>
                      )}
                      {payment.registrationYear && (
                        <div className="payment-year">Year: {payment.registrationYear}</div>
                      )}
                    </div>
                    {isRecent && (
                      <div className="recent-badge">Most Recent Payment</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="actions">
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            Go Home
          </button>
          {registrationId && (
            <button
              onClick={() => navigate(`/admin/registration-details/${registrationId}`)}
              className="btn btn-primary"
            >
              View Registration
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;

